/**
 * Tree maths for the "Move to..." dialog.
 *
 * Kept out of the component so the rules that are easy to get subtly wrong -
 * excluding a folder from its own subtree, nested search matches, indentation
 * depth - can be tested on their own.
 */

const nameOf = (folder) => folder?.name || "";

const byName = (a, b) =>
  nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base" });

/**
 * Flattens the folder list into indented rows.
 *
 * When a folder is the thing being moved, it and its whole subtree are left out
 * - a folder cannot be moved inside itself.
 *
 * @param {Array}  folders raw folders from the API ({ _id, name, parent })
 * @param {object} item    the item being moved ({ id, type })
 * @returns {Array} rows of { id, name, depth, parent, hasChildren, path }
 */
export const buildFolderOptions = (folders = [], item = null) => {
  const excluded = new Set();

  if (item?.type === "folder" && item?.id) {
    excluded.add(String(item.id));
    let added = true;
    while (added) {
      added = false;
      for (const folder of folders) {
        const id = String(folder._id);
        const parent = folder.parent ? String(folder.parent) : null;
        if (!excluded.has(id) && parent && excluded.has(parent)) {
          excluded.add(id);
          added = true;
        }
      }
    }
  }

  const byParent = {};
  for (const folder of folders) {
    if (excluded.has(String(folder._id))) continue;
    const key = folder.parent ? String(folder.parent) : "root";
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(folder);
  }
  Object.values(byParent).forEach((list) => list.sort(byName));

  const rows = [];
  const walk = (parentKey, depth, parentId, ancestorNames) => {
    (byParent[parentKey] || []).forEach((folder) => {
      const id = String(folder._id);
      rows.push({
        id,
        name: folder.name,
        depth,
        parent: parentId,
        hasChildren: (byParent[id] || []).length > 0,
        // Full location, shown next to a search result
        path: ["My Folder", ...ancestorNames].join(" / "),
      });
      walk(id, depth + 1, id, [...ancestorNames, folder.name]);
    });
  };
  walk("root", 0, null, []);

  return rows;
};

const indexOptions = (options) => {
  const byId = new Map(options.map((opt) => [opt.id, opt]));
  const childrenById = new Map();

  options.forEach((opt) => {
    const key = opt.parent || "root";
    if (!childrenById.has(key)) childrenById.set(key, []);
    childrenById.get(key).push(opt);
  });

  const ancestorIdsOf = (opt) => {
    const chain = [];
    let parent = opt.parent;
    while (parent) {
      chain.push(parent);
      parent = byId.get(parent)?.parent || null;
    }
    return chain;
  };

  return { byId, childrenById, ancestorIdsOf };
};

/**
 * Which folders match the search term, and which of those head their own
 * result tree.
 *
 * A match nested inside another match is not a root: it will already be shown
 * underneath its matching ancestor, and listing it twice is confusing.
 *
 * @returns {{matches: Set<string>, roots: Array}}
 */
export const findSearchRoots = (options = [], search = "") => {
  const term = String(search).trim().toLowerCase();
  if (!term) return { matches: new Set(), roots: [] };

  const { ancestorIdsOf } = indexOptions(options);

  const matches = new Set(
    options
      .filter((opt) => nameOf(opt).toLowerCase().includes(term))
      .map((opt) => opt.id)
  );

  const roots = options.filter(
    (opt) =>
      matches.has(opt.id) && !ancestorIdsOf(opt).some((id) => matches.has(id))
  );

  return { matches, roots };
};

/**
 * The rows to render.
 *
 * Without a search term this is the normal tree: a row shows once every folder
 * above it is expanded. With a search term it is each matching folder plus
 * whatever of its subtree is expanded - so a match with sub-folders can still
 * be drilled into and a sub-folder picked as the destination. Depth is measured
 * from the match, not from the root, so the indentation stays shallow.
 *
 * @param {object}      params
 * @param {Array}       params.options  from buildFolderOptions
 * @param {string}      params.search
 * @param {Set<string>} params.expanded ids of expanded folders
 * @param {Array}       [params.roots]  from findSearchRoots, recomputed if absent
 */
export const buildVisibleRows = ({
  options = [],
  search = "",
  expanded = new Set(),
  roots,
}) => {
  const term = String(search).trim().toLowerCase();
  const { childrenById, ancestorIdsOf } = indexOptions(options);

  if (!term) {
    return options.filter((opt) =>
      ancestorIdsOf(opt).every((id) => expanded.has(id))
    );
  }

  const searchRoots = roots || findSearchRoots(options, search).roots;
  const rows = [];

  const pushSubtree = (opt, depth) => {
    rows.push({ ...opt, depth });
    if (!expanded.has(opt.id)) return;
    (childrenById.get(opt.id) || []).forEach((child) =>
      pushSubtree(child, depth + 1)
    );
  };

  searchRoots.forEach((root) => pushSubtree(root, 0));
  return rows;
};

const moveToTree = { buildFolderOptions, buildVisibleRows, findSearchRoots };

export default moveToTree;
